/**
 * Types du contrat, ré-exportés sous des noms courts.
 *
 * Les écrans importent d'ici et jamais de `./generated/schema` : le jour où le
 * générateur change de forme, c'est ce fichier qui absorbe le choc, pas cinq
 * écrans.
 */
import type { components } from "./generated/schema";

/**
 * Entier 64 bits transporté en **chaîne décimale**.
 *
 * Ce n'est pas une coquetterie du contrat : un nombre JSON est lu par le
 * navigateur comme un double IEEE-754, qui perd la précision au-delà de 2^53.
 * Un `Number(...)` sur un montant réintroduit exactement l'arrondi silencieux
 * que tout le projet s'interdit.
 *
 * Règle : afficher tel quel (via `format/`), calculer en `BigInt` (via
 * `format/amount.ts`), ne jamais convertir en `number`.
 */
export type Int64 = components["schemas"]["Int64"];

export type Uuid = components["schemas"]["Uuid"];

/** L'un des quatre seaux du grand livre. */
export type Bucket = components["schemas"]["Bucket"];
/** Nature d'une écriture : purchase, reserve, consume, release, expire, adjust. */
export type EntryKind = components["schemas"]["EntryKind"];
/** Statut d'une mission : ordered, completed, cancelled. */
export type MissionStatus = components["schemas"]["MissionStatus"];

export type Pack = components["schemas"]["Pack"];
export type PacksResponse = components["schemas"]["PacksResponse"];
export type Expertise = components["schemas"]["Expertise"];
export type PublishGridRequest = components["schemas"]["PublishGridRequest"];
export type Grid = components["schemas"]["Grid"];
export type Entry = components["schemas"]["Entry"];
export type Receipt = components["schemas"]["Receipt"];
export type Balance = components["schemas"]["Balance"];
export type Anomaly = components["schemas"]["Anomaly"];
export type WalletState = components["schemas"]["WalletState"];
export type History = components["schemas"]["History"];
export type Mission = components["schemas"]["Mission"];
export type MissionProgress = components["schemas"]["MissionProgress"];
export type WalletMissions = components["schemas"]["WalletMissions"];
/** Un mois civil du contrat, « 2026-09 ». */
export type Month = components["schemas"]["Month"];
export type MonthlyFlow = components["schemas"]["MonthlyFlow"];
export type WalletConsumption = components["schemas"]["WalletConsumption"];
export type Problem = components["schemas"]["Problem"];

/**
 * Rôle d'un compte : esn_admin, commercial (ESN) ; consultant (intervenant de
 * l'ESN, sans aucun droit sur l'argent) ; admin, member (entreprise).
 */
export type Role = components["schemas"]["Role"];
/** Statut d'un compte : invited, active, disabled. */
export type UserStatus = components["schemas"]["UserStatus"];
export type User = components["schemas"]["User"];
export type UsersResponse = components["schemas"]["UsersResponse"];
/** État de connexion d'un compte, pour l'administrateur ESN. */
export type ConnectionSummary = components["schemas"]["ConnectionSummary"];
export type ConnectionsResponse = components["schemas"]["ConnectionsResponse"];
/** Une ligne d'historique : connexion, échec, déconnexion, invitation acceptée, réinitialisation. */
export type ConnectionEvent = components["schemas"]["ConnectionEvent"];
export type ConnectionHistory = components["schemas"]["ConnectionHistory"];
export type Company = components["schemas"]["Company"];
export type CompaniesResponse = components["schemas"]["CompaniesResponse"];
export type Permissions = components["schemas"]["Permissions"];
export type Me = components["schemas"]["Me"];
export type InvitationPreview = components["schemas"]["InvitationPreview"];
export type Invited = components["schemas"]["Invited"];
export type CreateCompanyResponse = components["schemas"]["CreateCompanyResponse"];
/** Statut d'une demande d'inscription : pending, accepted, rejected. */
export type SignupStatus = components["schemas"]["SignupStatus"];
export type Signup = components["schemas"]["Signup"];
export type SignupsResponse = components["schemas"]["SignupsResponse"];
export type SignupRequestBody = components["schemas"]["SignupRequestBody"];
export type RejectSignupRequest = components["schemas"]["RejectSignupRequest"];
export type AcceptSignupResponse = components["schemas"]["AcceptSignupResponse"];

export type LoginRequest = components["schemas"]["LoginRequest"];
export type ForgotPasswordRequest = components["schemas"]["ForgotPasswordRequest"];
export type ResetPasswordRequest = components["schemas"]["ResetPasswordRequest"];
export type InvitationLookupRequest = components["schemas"]["InvitationLookupRequest"];
export type AcceptInvitationRequest = components["schemas"]["AcceptInvitationRequest"];
export type CreateCompanyRequest = components["schemas"]["CreateCompanyRequest"];
export type UpdateBrandingRequest = components["schemas"]["UpdateBrandingRequest"];
export type CompanySettingsRequest = components["schemas"]["CompanySettingsRequest"];
/** Prélèvement automatique d'une entreprise : moyen, pay as you go, limite. */
export type BillingAccount = components["schemas"]["BillingAccount"];
export type UpdateBillingRequest = components["schemas"]["UpdateBillingRequest"];
export type CreditLimitRequest = components["schemas"]["CreditLimitRequest"];
export type BillingSetup = components["schemas"]["BillingSetup"];
/** Réglage de la recharge automatique : seuil en centièmes de token, pack racheté. */
export type TopUpRequest = components["schemas"]["TopUpRequest"];
/** Un prélèvement : recharge automatique ou achat de pack. */
export type Charge = components["schemas"]["Charge"];
export type ChargesResponse = components["schemas"]["Charges"];
export type InviteRequest = components["schemas"]["InviteRequest"];
export type ChangeRoleRequest = components["schemas"]["ChangeRoleRequest"];

/** Statut d'une feuille de temps : draft, submitted, approved, rejected. */
export type TimesheetStatus = components["schemas"]["TimesheetStatus"];
/**
 * Une mission vue par son consultant. Le type n'a AUCUN champ de montant, et
 * c'est le contrat qui le garantit : rien à masquer côté écran.
 */
export type MissionForConsultant = components["schemas"]["MissionForConsultant"];
export type MissionsForConsultantResponse = components["schemas"]["MissionsForConsultantResponse"];
export type TimesheetEntry = components["schemas"]["TimesheetEntry"];
/**
 * Une feuille complète. `estimated_tokens` et `approved_tokens` sont absents
 * de la réponse rendue à un consultant : les tester avec `!== undefined`,
 * jamais les supposer.
 */
export type Timesheet = components["schemas"]["Timesheet"];
export type TimesheetSummary = components["schemas"]["TimesheetSummary"];
export type TimesheetsResponse = components["schemas"]["TimesheetsResponse"];
export type RecipientsResponse = components["schemas"]["RecipientsResponse"];
export type OpenTimesheetRequest = components["schemas"]["OpenTimesheetRequest"];
export type SaveEntriesRequest = components["schemas"]["SaveEntriesRequest"];
export type RejectTimesheetRequest = components["schemas"]["RejectTimesheetRequest"];
export type SalesRepRequest = components["schemas"]["SalesRepRequest"];
export type AssignRequest = components["schemas"]["AssignRequest"];
export type AssignmentRateRequest = components["schemas"]["AssignmentRateRequest"];
export type Assignment = components["schemas"]["Assignment"];
export type TeamResponse = components["schemas"]["TeamResponse"];
export type TeamMember = TeamResponse["assignments"][number];
export type Staffing = components["schemas"]["Staffing"];
export type StaffingResponse = components["schemas"]["StaffingResponse"];
export type SetRecipientsRequest = components["schemas"]["SetRecipientsRequest"];
export type InviteConsultantRequest = components["schemas"]["InviteConsultantRequest"];

export type CreditPackRequest = components["schemas"]["CreditPackRequest"];
export type OrderMissionRequest = components["schemas"]["OrderMissionRequest"];
export type ConsumeMissionRequest = components["schemas"]["ConsumeMissionRequest"];
export type CompleteMissionRequest = components["schemas"]["CompleteMissionRequest"];

/** Réponse de la sonde d'exploitation. */
export type Health = { status: "ok" };

/** Les quatre seaux, dans l'ordre où on les affiche partout. */
export const BUCKETS = ["available", "reserved", "consumed", "expired"] as const;

/** Libellés français des seaux, à afficher tels quels. */
export const BUCKET_LABELS: Record<Bucket, string> = {
  available: "Disponible",
  reserved: "Réservé",
  consumed: "Consommé",
  expired: "Expiré",
};

/** Libellés français des natures d'écriture. */
export const ENTRY_KIND_LABELS: Record<EntryKind, string> = {
  purchase: "Achat",
  reserve: "Réservation",
  consume: "Consommation",
  release: "Libération",
  expire: "Expiration",
  adjust: "Ajustement",
};

/** Libellés français des statuts de mission. */
export const MISSION_STATUS_LABELS: Record<MissionStatus, string> = {
  ordered: "En cours",
  completed: "Close",
  cancelled: "Annulée",
};

/** Libellés français des rôles. */
export const ROLE_LABELS: Record<Role, string> = {
  esn_admin: "Administrateur ESN",
  commercial: "Commercial",
  consultant: "Consultant",
  admin: "Administrateur",
  member: "Membre",
};

/** Libellés français des statuts de compte. */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  invited: "Invitation en attente",
  active: "Actif",
  disabled: "Retiré",
};

/** Libellés français des statuts de demande d'inscription. */
export const SIGNUP_STATUS_LABELS: Record<SignupStatus, string> = {
  pending: "En attente",
  accepted: "Acceptée",
  rejected: "Refusée",
};

/** Libellés français des statuts de feuille de temps. */
export const TIMESHEET_STATUS_LABELS: Record<TimesheetStatus, string> = {
  draft: "Brouillon",
  submitted: "Soumise",
  approved: "Approuvée",
  rejected: "Refusée",
};

export type Branding = components["schemas"]["Branding"];
export type UpdatePlatformBrandingRequest = components["schemas"]["UpdatePlatformBrandingRequest"];
